import {
	DetailsBox,
	DetailsBoxContent,
	GreyBox,
} from '@dealstack/frontend/shared/components/DocumentViewerV2/common/commonComponents.tsx';
import { DocumentViewerHeader } from '@dealstack/frontend/shared/components/DocumentViewerV2/common/DocumentViewerHeader.tsx';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile.ts';
import {
	useTranslationCommonUi,
	useTranslationDefault,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common';
import { getValidFileNameForDownload } from '@dealstack/shared/lib/common/file/file';
import { Flex, Text } from '@mantine/core';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, PasswordResponses, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import styled from 'styled-components';
import { HIGHLIGHTED_CLASS_NAME } from './pdf-constants.ts';
import type { PdfViewerProps } from './PdfViewer.types';
import { usePdfDocumentKey } from '@dealstack/frontend/shared/hooks/usePdfDocumentKey';
import { usePdfPageState } from './usePdfPageState.ts';
import { PDF_DOCUMENT_OPTIONS, PDF_WORKER_SRC } from '@dealstack/frontend/shared/utils/pdfWorker';

pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
const PdfViewer = forwardRef<HTMLDivElement, PdfViewerProps>(
	(
		{
			fileName,
			fileTitleComponent,
			file,
			className,
			renderForms,
			renderTextLayer = false,
			details,
			sectionAboveDocument,
			isLoading,
			showDetailsByDefault = false,
			showBackButton = true,
			onDocumentClick,
			onBackArrowClick,
			onCancelPassword,
			onDownload: onDownloadCallback,
			pageProps,
		},
		ref
	) => {
		const tCommonUi = useTranslationCommonUi();
		const tDefault = useTranslationDefault();

		const [scale, setScale] = useState(1);
		const [showDetails, setShowDetails] = useState(showDetailsByDefault);
		const documentKey = usePdfDocumentKey(file, fileName);
		const { totalPages, setTotalPages, currentPage, setCurrentPage } =
			usePdfPageState(documentKey);

		const onPrint = async () => {
			if (!file) {
				return;
			}

			const isUrlString = typeof file === 'string';

			if (isUrlString) {
				const response = await fetch(file);
				if (!response.ok) {
					throw new Error(`Failed to fetch file: ${response.statusText}`);
				}

				const blob = await response.blob();

				const blobWithoutContentDispositionHeaderToAvoidDownloading = new Blob([blob], {
					type: 'application/pdf',
				});

				const blobUrl = window.URL.createObjectURL(
					blobWithoutContentDispositionHeaderToAvoidDownloading
				);
				window.open(blobUrl, '_blank');
				return;
			}

			const blob = new Blob([file], { type: 'application/pdf' });
			const blobUrl = window.URL.createObjectURL(blob);
			window.open(blobUrl);
		};

		const onDownload = () => {
			if (!file) {
				return;
			}

			onDownloadCallback?.();

			if (typeof file === 'string') {
				window.open(file);
			} else {
				const fileNameToDownload = getValidFileNameForDownload(fileName, FileExtension.pdf);
				downloadFileFromBlob(file, fileNameToDownload);
			}
		};

		const onPassword = (callback, reason) => {
			const callbackProxy = (password) => {
				if (password === null) {
					onCancelPassword?.();
				} else {
					callback(password);
				}
			};

			switch (reason) {
				case PasswordResponses.NEED_PASSWORD: {
					const password = prompt(tCommonUi('prompt.need_password'));
					callbackProxy(password);
					break;
				}
				case PasswordResponses.INCORRECT_PASSWORD: {
					const password = prompt(tCommonUi('prompt.incorrect_password'));
					callbackProxy(password);
					break;
				}
				default:
			}
		};

		// --- scrolling + current page tracking ---
		const pagesScrollRef = useRef<HTMLDivElement>(null);
		const pagesRefs = useRef<Record<string, HTMLDivElement | null>>({});
		// Memoised because the page tracker below depends on it: a fresh array every
		// render would re-attach the scroll listener on every render.
		const pagesNumbers = useMemo(
			() => Array.from({ length: totalPages }, (_, index) => index + 1),
			[totalPages]
		);

		useEffect(() => {
			const container = pagesScrollRef.current;
			if (!container) {
				return;
			}

			const onScroll = () => {
				const containerTop = container.getBoundingClientRect().top;

				let topMostPage = 1;
				// makes sure it will always be overwritten on the first iteration
				let minDistance = Infinity;

				pagesNumbers.forEach((id) => {
					const currentPage = pagesRefs.current[id];
					if (!currentPage) {
						return;
					}
					const currentPageRect = currentPage.getBoundingClientRect();
					// get the page with the smallest distance to the top of the container
					const distance = Math.abs(currentPageRect.top - containerTop);
					if (distance < minDistance) {
						minDistance = distance;
						topMostPage = id;
					}
				});

				setCurrentPage(topMostPage);
			};

			container.addEventListener('scroll', onScroll, { passive: true });
			onScroll();

			return () => container.removeEventListener('scroll', onScroll);
		}, [pagesNumbers]);

		// --- scaling ---
		const [scrollPositionBeforeScaleChange, setScrollPositionBeforeScaleChange] = useState(0);

		useEffect(() => {
			if (!pagesScrollRef.current) {
				return;
			}
			pagesScrollRef.current.scrollTop = scrollPositionBeforeScaleChange;
		}, [scale, scrollPositionBeforeScaleChange]);

		const onScaleChange = (increase: boolean) => {
			const scrollPosition = pagesScrollRef.current?.scrollTop ?? 0;
			setScrollPositionBeforeScaleChange(scrollPosition);
			setScale((prev) => (increase ? prev + 0.2 : prev - 0.2));
		};

		return (
			<Flex
				className={className}
				ref={ref}
				direction="column"
				gap="1rem"
				sx={{ flexGrow: 1, overflow: 'hidden' }}
			>
				<DocumentViewerHeader
					title={fileTitleComponent ?? fileName}
					currentPage={totalPages ? currentPage : 0}
					totalPages={totalPages}
					onBackArrowClick={onBackArrowClick}
					onUpscale={() => onScaleChange(true)}
					onDownscale={() => onScaleChange(false)}
					onDownload={onDownload}
					onPrint={onPrint}
					onToggleDetails={details ? () => setShowDetails(!showDetails) : undefined}
					showBackButton={showBackButton}
				/>

				{!!sectionAboveDocument && sectionAboveDocument}

				<Flex gap="0.5rem" style={{ flexGrow: 1, overflow: 'hidden' }}>
					<GreyBox
						ref={pagesScrollRef}
						style={{
							flexGrow: 1,
							display: 'flex',
							justifyContent: 'center',
							overflow: 'auto',
							position: 'relative',
						}}
					>
						{isLoading || !file ? (
							<InlineLoader />
						) : (
							<Document
								options={PDF_DOCUMENT_OPTIONS}
								key={documentKey}
								file={file}
								onLoadSuccess={({ numPages }) => {
									setTotalPages(numPages);
								}}
								loading={<InlineLoader />}
								error={
									<EmptyState
										icon="es-box-magnifying-glass"
										title={tDefault('no_data')}
										description={tCommonUi('pdf_loader.failed_load')}
										style={{ minWidth: '500px' }}
									/>
								}
								noData={
									<EmptyState
										icon="es-box-magnifying-glass"
										title={tDefault('no_data')}
										description={tCommonUi('pdf_loader.failed_load')}
										style={{ minWidth: '500px' }}
									/>
								}
								onPassword={onPassword}
								onClick={onDocumentClick}
							>
								<Flex direction="column" gap="0.5rem" pb="1rem">
									{totalPages > 0 &&
										pagesNumbers.map((pageNumber) => (
											<div
												key={pageNumber}
												id={pageNumber.toString()}
												ref={(ref) => (pagesRefs.current[pageNumber] = ref)}
											>
												<Page
													scale={scale}
													key={pageNumber}
													pageNumber={pageNumber}
													renderTextLayer={renderTextLayer}
													renderForms={renderForms}
													{...(pageProps ?? {})}
												/>
											</div>
										))}
								</Flex>
							</Document>
						)}
					</GreyBox>
					{showDetails && !!details && (
						<DetailsBox>
							<Flex
								justify="space-between"
								p="1rem"
								sx={{
									borderBottom: `1px solid ${defaultTheme.v3.colors.gray[2]}`,
								}}
							>
								<Text size="B5Bold">{tCommonUi('preview_pdf.details')}</Text>
								<Icon
									variant="close-v3"
									size="lg"
									onClick={() => setShowDetails(false)}
								/>
							</Flex>
							<DetailsBoxContent>{details}</DetailsBoxContent>
						</DetailsBox>
					)}
				</Flex>
			</Flex>
		);
	}
);

const Styled = styled(PdfViewer)`
	.${HIGHLIGHTED_CLASS_NAME} {
		border: 2px solid ${(props) => props.theme.v3.colors.green[8]} !important;
	}
`;

export { Styled as PdfViewer };
